import React, { useEffect, useState } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';
import {
  getLungScreeningEligibility,
  SYMPTOM_OPTIONS,
  SDM_CHECKLIST_ITEMS
} from '../generators/utils/lungScreeningEligibility';

const SMOKING_STATUS_OPTIONS = ['Current smoker', 'Former smoker', 'Never smoked'];
const SCREENING_TYPE_OPTIONS = ['Initial screening', 'Annual / subsequent screening'];

const INITIAL_LUNG_SCREENING_DATA = {
  examType: 'Lung Cancer Screening',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: 'Lung cancer screening',
  callReport: false,
  phoneNumber: '',
  comments: '',
  dateOfBirth: '',
  smokingStatus: '',
  packYears: '',
  yearsSinceQuit: '',
  symptoms: [],
  orderingProvider: '',
  screeningType: 'Initial screening',
  sdmVisit: {
    date: new Date().toISOString().split('T')[0],
    confirmedHighRiskCriteria: false,
    discussedBenefitsRisks: false,
    discussedFollowUp: false,
    discussedOverdiagnosisRisks: false,
    counseledAnnualScreening: false,
    counseledSmokingCessation: false
  }
};

function LungScreeningForm({ token, onBack, facilityInfo }) {
  const [activeTab, setActiveTab] = useState(0);
  const { technologists, defaultTech } = useTechnologists();
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_LUNG_SCREENING_DATA,
      technologist: defaultTech
    },
    'Lung Cancer Screening',
    {
      token, facilityInfo
    }
  );

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const handleSymptomToggle = (symptom) => {
    const current = formData.symptoms || [];
    const next = current.includes(symptom)
      ? current.filter(s => s !== symptom)
      : [...current, symptom];
    updateField('symptoms', next);
  };

  const handleSelectAllSDM = () => {
    const allChecked = SDM_CHECKLIST_ITEMS.reduce((acc, item) => {
      acc[item.key] = true;
      return acc;
    }, {});
    updateField('sdmVisit', { ...formData.sdmVisit, ...allChecked });
  };

  const eligibility = getLungScreeningEligibility(formData);

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} examLabel="Lung Cancer Screening" showCallReport={false} technologistLabel="Nurse" />

        {/* Risk Factors / Shared Decision-Making Visit tabs */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Risk Factors
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Shared Decision-Making Visit
            </button>
          </div>

          <div className="worksheet-tab-content">
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>Date of Birth: *</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => updateField('dateOfBirth', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Smoking Status: *</label>
                  <select
                    value={formData.smokingStatus}
                    onChange={(e) => updateField('smokingStatus', e.target.value)}
                  >
                    <option value="">Select status</option>
                    {SMOKING_STATUS_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Smoking History (pack-years): *</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="1 pack/day for 1 year = 1 pack-year"
                    value={formData.packYears}
                    onChange={(e) => updateField('packYears', e.target.value)}
                  />
                </div>

                {formData.smokingStatus === 'Former smoker' && (
                  <div className="form-group">
                    <label>Years Since Quitting: *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.yearsSinceQuit}
                      onChange={(e) => updateField('yearsSinceQuit', e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Current Signs/Symptoms of Lung Cancer:</label>
                  <div className="checkbox-group">
                    {SYMPTOM_OPTIONS.map(symptom => (
                      <label key={symptom}>
                        <input
                          type="checkbox"
                          checked={formData.symptoms.includes(symptom)}
                          onChange={() => handleSymptomToggle(symptom)}
                        />
                        {symptom}
                      </label>
                    ))}
                  </div>
                  <small>Leave all unchecked if the patient is asymptomatic — required for eligibility.</small>
                </div>
              </div>
            )}

            {activeTab === 1 && (
              <div>
                <p>Medicare requires a shared decision-making visit before the first LDCT screening.</p>
                <div className="form-group">
                  <label>Visit Date:</label>
                  <input
                    type="date"
                    value={formData.sdmVisit.date}
                    onChange={(e) => updateNestedField('sdmVisit.date', e.target.value)}
                  />
                </div>
                <button type="button" onClick={handleSelectAllSDM} className="select-all-btn">
                  Select All
                </button>
                <div className="checkbox-group checkbox-group-single">
                  {SDM_CHECKLIST_ITEMS.map(item => (
                    <label key={item.key}>
                      <input
                        type="checkbox"
                        checked={formData.sdmVisit[item.key]}
                        onChange={(e) => updateNestedField('sdmVisit', item.key, e.target.checked)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Eligibility Determination */}
        <div className="form-section">
          <h3>Eligibility Determination</h3>
          <div className={`lung-eligibility-banner ${eligibility.eligible ? 'eligible' : 'not-eligible'}`}>
            <div className="lung-eligibility-status">
              {eligibility.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
            </div>
            {eligibility.age !== null && (
              <div className="lung-eligibility-age">Calculated age: {eligibility.age}</div>
            )}
            {!eligibility.eligible && eligibility.reasons.length > 0 && (
              <ul className="lung-eligibility-reasons">
                {eligibility.reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
          <small>
            Based on CMS coverage criteria (NCD 210.14): age 50-77, asymptomatic, ≥20
            pack-year smoking history, currently smoking or quit within the last 15 years.
            This is a determination aid, not a substitute for clinical judgment.
          </small>
        </div>

        {/* Screening Details */}
        <div className="form-section">
          <h3>Screening Details</h3>
          <div className="tab-content-grid">
            <div className="form-group">
              <label>Screening Type:</label>
              <select
                value={formData.screeningType}
                onChange={(e) => updateField('screeningType', e.target.value)}
              >
                {SCREENING_TYPE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Ordering Provider:</label>
              <input
                type="text"
                value={formData.orderingProvider}
                onChange={(e) => updateField('orderingProvider', e.target.value)}
                placeholder="Provider name"
              />
            </div>
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="6"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '120px', fontSize: '16px' }}
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={handlePreview} disabled={isLoading} className="preview-btn">
            Preview Report
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button type="button" onClick={resetForm} disabled={isLoading} className="reset-btn">
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default LungScreeningForm;
